import { getChatMemberIdentifier } from 'app/shared/components/TeamChat/TeamChat.utils';
import _ from 'lodash';

import {
  BROWSER_NOTIFICATIONS_STORAGE_KEY,
  SOUND_NOTIFICATIONS_STORAGE_KEY,
} from 'hooks/useNotificationPref/constants';
import { readNotificationPref } from 'hooks/useNotificationPref/utils';

import {
  DEFAULT_BODY,
  MS_PER_SECOND,
  NOTIFICATION_TITLE,
  SOUND_DURATION_MS,
  SOUND_FREQUENCY_HZ,
  SOUND_GAIN,
  SOUND_GAIN_MIN,
} from './constants';

const getMessageBody = message => {
  const payload = message?.payload ?? null;

  if (payload?.type === 'File') {
    return `Sent a file: ${payload.fileName ?? ''}`.trim();
  }

  return payload?.message ?? DEFAULT_BODY;
};

const isSelfMessage = (message, loggedUserId) => {
  if (!loggedUserId) {
    return false;
  }

  const rawMemberId = message?.memberId ?? message?.MemberId ?? null;
  const senderUserId = getChatMemberIdentifier(rawMemberId);

  return !!senderUserId && senderUserId === loggedUserId;
};

export const maybeFireBrowserNotification = ({ detail, loggedUserId, activeChatId }) => {
  if (!readNotificationPref(BROWSER_NOTIFICATIONS_STORAGE_KEY)) {
    return;
  }

  if (typeof window === 'undefined' || !('Notification' in window)) {
    return;
  }

  if (window.Notification.permission !== 'granted') {
    return;
  }

  if (isSelfMessage(detail?.message, loggedUserId)) {
    return;
  }

  const chatId = detail?.chatId ?? null;
  const isActiveChannel = !!chatId && chatId === activeChatId;
  const isVisible = typeof document !== 'undefined' && !document.hidden;

  if (isActiveChannel && isVisible) {
    return;
  }

  const notification = new window.Notification(NOTIFICATION_TITLE, {
    body: getMessageBody(detail?.message),
    tag: `teamchat-${chatId ?? ''}`,
  });

  notification.onclick = () => {
    if (typeof window !== 'undefined' && _.isFunction(window.focus)) {
      window.focus();
    }

    notification.close();
  };
};

const playSound = audioContextRef => {
  const AudioContextCtor = window.AudioContext || window.webkitAudioContext;

  if (!AudioContextCtor) {
    return;
  }

  if (!audioContextRef.current) {
    try {
      audioContextRef.current = new AudioContextCtor();
    } catch {
      return;
    }
  }

  const ctx = audioContextRef.current;

  if (ctx.state === 'suspended') {
    ctx.resume().catch(_.noop);
  }

  const oscillator = ctx.createOscillator();
  const gain = ctx.createGain();

  const durationSeconds = SOUND_DURATION_MS / MS_PER_SECOND;

  oscillator.type = 'sine';
  oscillator.frequency.setValueAtTime(SOUND_FREQUENCY_HZ, ctx.currentTime);
  gain.gain.setValueAtTime(SOUND_GAIN, ctx.currentTime);
  gain.gain.exponentialRampToValueAtTime(SOUND_GAIN_MIN, ctx.currentTime + durationSeconds);

  oscillator.connect(gain);
  gain.connect(ctx.destination);

  oscillator.start();
  oscillator.stop(ctx.currentTime + durationSeconds);
};

export const maybePlaySound = ({ detail, loggedUserId, activeChatId, audioContextRef }) => {
  if (!readNotificationPref(SOUND_NOTIFICATIONS_STORAGE_KEY)) {
    return;
  }

  if (isSelfMessage(detail?.message, loggedUserId)) {
    return;
  }

  const chatId = detail?.chatId ?? null;
  const isActiveChannel = !!chatId && chatId === activeChatId;
  const isVisible = typeof document !== 'undefined' && !document.hidden;

  if (isActiveChannel && isVisible) {
    return;
  }

  playSound(audioContextRef);
};
